<script>
	import { onMount, onDestroy } from "svelte";
	import { navigate } from "svelte-routing";
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import queryString from "query-string";
	import client from "../../../services/client";
	import {
		errorMessage,
		sizeSelect,
		totalPages,
		loading,
		loadingStatus,
		initPage,
	} from "../../../store/store";
	import CarparkManagementTable from "./CarparkManagementTable.svelte";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import FilterForm from "../../../components/FilterForm/FilterForm.svelte";
	import SelectSize from "../../../components/SizeSelect/SizeSelect.svelte";
	import { extractErrorMessage, handleCsvImport } from "src/utils/misc";
	import { isCarparkCsvExportEnabled } from "src/config/flag-config";

	let data = [];

	const handleExportCSV = async () => {
		try {
			const res = await client("/v1/carParks/csvExport", {
				method: "GET",
				version: "Vehicle",
				header: "csv",
			});
			const blob = new Blob([res.data], { type: "text/csv" });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			const disposition = res.headers["content-disposition"];
			const rawFilename = disposition
				? disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1] ||
					disposition.match(/filename=([^;]+)/i)?.[1] ||
					"carparks.csv"
				: "carparks.csv";
			a.download = decodeURIComponent(
				rawFilename.trim().replace(/^"|"$/g, "")
			);
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			setTimeout(() => window.URL.revokeObjectURL(url), 0);
		} catch (err) {
			extractErrorMessage(err);
			console.log(err);
		}
	};

	//Start Logic for filter feature
	const params = new Proxy(new URLSearchParams(window.location.search), {
		get: (searchParams, prop) => searchParams.get(prop),
	});
	$: queryParams = `?page=1&size=${$sizeSelect}`;
	let searchParams = window.location.search.toString();
	let queryPage = searchParams.includes("page");
	let queryStatus = searchParams.includes("carparkStatus");
	let queryKeyword = searchParams.includes("keyword");
	let queryStartDateRange = searchParams.includes("startDateRange");
	let queryEndDateRange = searchParams.includes("endDateRange");

	$: if (queryPage) {
		queryParams = `?page=${params.page}&size=${$sizeSelect}`;
	}
	$: if (queryStatus) {
		queryParams = queryParams.concat(`&status=${params.carparkStatus}`);
	}
	$: if (queryKeyword) {
		queryParams = queryParams.concat(`&keyword=${params.keyword}`);
	}
	$: if (queryStartDateRange) {
		queryParams = queryParams.concat(`&startDate=${params.startDateRange}`);
	}
	$: if (queryEndDateRange) {
		queryParams = queryParams.concat(`&endDate=${params.endDateRange}`);
	}

	//End Logic for filter feature

	async function fetchCarparks() {
		try {
			loading.set(true);
			const res = await client(`/v1/carParks/filter${queryParams}`, {
				method: "GET",
				version: "Vehicle",
			});
			data = res.data.dataList;
			data.totalCount = res.data.totalCount;
			totalPages.set(res.data.totalPage);
			if (res.data.dataList) {
				loading.set(false);
				loadingStatus.set(false);
			}
		} catch (err) {
			loading.set(false);
			extractErrorMessage(err);
			console.log(err.response);
		}
	}

	$: ($sizeSelect, trigerSelectCarpark());

	onDestroy(() => {
		initPage.set(1);
		sizeSelect.set("20");
		totalPages.set(1);
	});

	const trigerSelectCarpark = async (e) => {
		if (e === "update") {
			loadingStatus.set(true);
		} else {
			loading.set(true);
		}
		fetchCarparks();
	};

	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});
</script>

<Row>
	<Col xs={12}>
		<div>
			<ErrorMessageHandler message={err} />
			<Row className="align-items-center mb-3">
				<Col>
					<PageHeader title="Car park management" />
				</Col>
				<Col
					xs="auto"
					class="d-flex justify-content-end align-items-center"
				>
					<div class="d-flex align-items-start">
						{#if isCarparkCsvExportEnabled}
							<div class="mx-2 d-flex align-items-center">
								<Button
									class="themeButton primary px-4"
									on:click={() => handleExportCSV()}
									>Export CSV</Button
								>
							</div>
						{/if}
						<div class="mx-2">
							<input
								id="fileInput"
								type="file"
								multiple={false}
								hidden="hidden"
								on:change={(e) =>
									handleCsvImport(
										e,
										"/v1/carParks/csvFileImport",
										"Vehicle"
									)}
							/>
							<label
								type="button"
								for="fileInput"
								id="customButton"
								class="btn themeButton primary px-4"
								block>Import CSV</label
							>
							<p>
								<a
									href="/assets/csv/Carpark_Template.csv"
									target="_blank"
									download="carpark_template.csv"
									rel="noreferrer"
									class="link link-text"
								>
									Download CSV Template
								</a>
							</p>
						</div>
						<div class="mx-2">
							<Button
								class="themeButton primary px-4"
								on:click={() =>
									navigate("/carpark-management/create")}
								>Add new car park</Button
							>
						</div>
					</div>
				</Col>
			</Row>
			<div class="d-flex flex-row justify-content-between">
				<Col xs={6}>
					<FilterForm
						keyword
						carparkStatus
						startDateRange
						endDateRange
					/>
				</Col>
				<div class="align-self-end">
					<SelectSize trigerSelect={fetchCarparks} />
				</div>
			</div>
		</div>
	</Col>
	<Col class="my-2">
		<CarparkManagementTable {data} {trigerSelectCarpark} />
	</Col>
</Row>
